<template>
    <div class="ep" @click.stop>
        <div class="ep__search-row">
            <input
                ref="searchInput"
                v-model="searchQuery"
                class="ep__search"
                placeholder="Buscar emoji..."
                autocomplete="off"
            />
            <button
                class="ep__close"
                type="button"
                title="Cerrar"
                @click="$emit('close')"
            >×</button>
        </div>

        <div v-if="!searchQuery" class="ep__tabs">
            <button
                v-for="cat in visibleCategories"
                :key="cat.id"
                :class="['ep__tab', { 'ep__tab--active': activeCategoryId === cat.id }]"
                :title="cat.name"
                type="button"
                @click="ScrollToCategory(cat.id)"
            >
                {{ cat.icon }}
            </button>
        </div>

        <div ref="scrollEl" class="ep__scroll" @scroll.passive="OnScroll">
            <template v-if="searchQuery">
                <div v-if="searchResults.length === 0" class="ep__empty">
                    No se encontraron emojis para "{{ searchQuery }}"
                </div>
                <div v-else class="ep__grid">
                    <button
                        v-for="item in searchResults"
                        :key="item.emoji"
                        class="ep__emoji-btn"
                        type="button"
                        @click="SelectEmoji(item)"
                    >{{ item.emoji }}</button>
                </div>
            </template>

            <template v-else>
                <div
                    v-for="cat in visibleCategories"
                    :key="cat.id"
                    :ref="el => RegisterSection(el, cat.id)"
                    class="ep__section"
                >
                    <div class="ep__section-title">{{ cat.name }}</div>

                    <div v-if="cat.emojis.length === 0" class="ep__empty-cat">
                        Sin emojis recientes
                    </div>
                    <div v-else class="ep__grid">
                        <button
                            v-for="item in cat.emojis"
                            :key="item.emoji"
                            class="ep__emoji-btn"
                            type="button"
                            @click="SelectEmoji(item)"
                        >{{ item.emoji }}</button>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { EMOJI_CATEGORIES, loadFrequentEmojis, saveFrequentEmoji } from './emoji-data.js';

export default defineComponent({
    name: 'EmojiPicker',
    emits: ['select', 'close'],
    data() {
        return {
            searchQuery: '',
            activeCategoryId: '',
            /** @type {Map<string, Element>} Used only for scroll-to navigation. */
            sectionRefs: new Map(),
            categories: [],
        };
    },
    computed: {
        visibleCategories() {
            return this.categories.filter(c => c.id !== 'frequent' || c.emojis.length > 0);
        },
        searchResults() {
            if (!this.searchQuery) return [];
            const q = this.searchQuery.toLowerCase().trim();
            const results = [];
            for (const cat of EMOJI_CATEGORIES) {
                if (cat.id === 'frequent') continue;
                for (const emoji of cat.emojis) {
                    if (
                        emoji.name.includes(q) ||
                        emoji.keywords.some(k => k.includes(q)) ||
                        emoji.emoji === q
                    ) {
                        results.push(emoji);
                    }
                }
            }
            return results.slice(0, 80);
        },
    },
    created() {
        // Non-reactive scroll state — no need to go through Vue
        this._isScrolling = false;
        this._scrollEndTimer = null;
        this._tabRafHandle = null;

        this._BuildCategories();

        const first = this.visibleCategories[0];
        if (first) this.activeCategoryId = first.id;
    },
    unmounted() {
        if (this._scrollEndTimer) clearTimeout(this._scrollEndTimer);
        if (this._tabRafHandle) cancelAnimationFrame(this._tabRafHandle);
    },
    methods: {
        _BuildCategories() {
            const frequent = loadFrequentEmojis();
            this.categories = EMOJI_CATEGORIES.map(cat => {
                if (cat.id === 'frequent') return { ...cat, emojis: [...frequent] };
                return cat;
            });
        },

        RegisterSection(el, catId) {
            if (el) {
                this.sectionRefs.set(catId, el);
            } else {
                this.sectionRefs.delete(catId);
            }
        },

        ScrollToCategory(catId) {
            this.activeCategoryId = catId;
            const el = this.sectionRefs.get(catId);
            if (el && this.$refs.scrollEl) {
                this.$refs.scrollEl.scrollTop = el.offsetTop - 4;
            }
        },

        OnScroll() {
            const scroll = this.$refs.scrollEl;
            if (!scroll) return;

            // Disable pointer events on first scroll event of a gesture.
            // Prevents the browser from recalculating hover states on every pixel.
            if (!this._isScrolling) {
                this._isScrolling = true;
                scroll.classList.add('ep__scroll--scrolling');
            }

            // Re-enable pointer events 150 ms after the last scroll event.
            if (this._scrollEndTimer) clearTimeout(this._scrollEndTimer);
            this._scrollEndTimer = setTimeout(() => {
                this._isScrolling = false;
                scroll.classList.remove('ep__scroll--scrolling');
            }, 150);

            // Update the active tab once per animation frame — no setTimeout lag.
            if (this._tabRafHandle) cancelAnimationFrame(this._tabRafHandle);
            this._tabRafHandle = requestAnimationFrame(() => {
                this._tabRafHandle = null;
                this._UpdateActiveTab();
            });
        },

        _UpdateActiveTab() {
            const container = this.$refs.scrollEl;
            if (!container) return;
            const scrollTop = container.scrollTop;
            let current = this.visibleCategories[0]?.id ?? '';
            this.sectionRefs.forEach((el, catId) => {
                if (el.offsetTop <= scrollTop + 40) current = catId;
            });
            this.activeCategoryId = current;
        },

        SelectEmoji(emojiObj) {
            saveFrequentEmoji(emojiObj);
            this.$emit('select', { i: emojiObj.emoji });
        },
    },
});
</script>

<style scoped>
.ep {
    width: 340px;
    height: 400px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    user-select: none;
    font-family: inherit;
}

.ep__search-row {
    padding: 10px 10px 6px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    gap: 6px;
}

.ep__close {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    font-size: 32px;
    line-height: 1;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    color: #555;
    flex-shrink: 0;
}

.ep__close:hover {
    background-color: #f0edfb;
}

.ep__search {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 13px;
    font-family: inherit;
    outline: none;
    transition: border-color 0.15s;
}

.ep__search:focus {
    border-color: var(--LativoDarkPurple, #6c3fc5);
}

.ep__tabs {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 4px 6px;
    border-bottom: 1px solid #f0f0f0;
    gap: 2px;
    scrollbar-width: none;
}

.ep__tabs::-webkit-scrollbar {
    display: none;
}

.ep__tab {
    flex-shrink: 0;
    background: none;
    border: none;
    border-radius: 6px;
    padding: 4px 5px;
    font-size: 16px;
    cursor: pointer;
    line-height: 1;
    transition: background-color 0.12s;
    opacity: 0.6;
}

.ep__tab:hover {
    background-color: #f5f5f5;
    opacity: 1;
}

.ep__tab--active {
    background-color: #ede9f8;
    opacity: 1;
}

.ep__scroll {
    overflow-y: auto;
    flex: 1;
    padding: 0 6px 6px;
    /* Own compositing layer — scroll handled by GPU, not main thread */
    will-change: transform;
    overscroll-behavior: contain;
}

/*
 * content-visibility: auto — the browser skips layout, paint, and compositing
 * for off-screen sections entirely. No JS needed.
 *
 * contain-intrinsic-size: auto <fallback> — on first render the browser uses
 * the fallback (200px). After the section is rendered once, it remembers the
 * actual size ("auto" keyword) so the scrollbar stays accurate.
 *
 * contain: layout style — even for on-screen sections, isolates layout
 * recalculations so a change inside one section can't trigger reflow of others.
 */
.ep__section {
    content-visibility: auto;
    contain-intrinsic-size: auto 200px;
    contain: layout style;
    padding-top: 8px;
}

.ep__section-title {
    font-size: 11px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 4px 4px;
}

.ep__grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 2px;
    align-content: flex-start;
}

.ep__emoji-btn {
    background: none;
    border: none;
    border-radius: 6px;
    padding: 4px;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    width: 100%;
    aspect-ratio: 1 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
    /* No 'transform' — avoids triggering compositing on every hover */
    transition: background-color 0.1s;
}

.ep__emoji-btn:hover {
    background-color: #f0edfb;
}

.ep__empty {
    padding: 24px 12px;
    text-align: center;
    color: #aaa;
    font-size: 13px;
}

.ep__empty-cat {
    padding: 6px 4px;
    color: #bbb;
    font-size: 12px;
}

/*
 * While scrolling: disable pointer events on all emoji buttons.
 * Prevents the browser from hit-testing hundreds of buttons on every pixel
 * of scroll movement, which is a major source of main-thread jank.
 */
.ep__scroll--scrolling .ep__emoji-btn {
    pointer-events: none;
}

@media (max-width: 600px) {
    .ep {
        width: 100%;
        max-width: 100%;
        height: 70vh;
        max-height: 70vh;
        border-radius: 16px 16px 0 0;
    }

    .ep__grid {
        grid-template-columns: repeat(7, 1fr);
    }
}
</style>
